// src/components/mitchRoom/ModelOutputs.js
import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { Loader2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { AuthContext } from '../../context/AuthContext';
import { LeagueContext } from '../../context/LeagueContext';
import {
  getModellingOutputs,
  getTeamPredictions,
  getPlayerAccuracySummary,
} from '../../services/api';
import LoadingState from '../shared/LoadingState';
import FilterSelect from './modelOutputs/FilterSelect';
import TeamResultsGrid from './modelOutputs/TeamResultsGrid';
import TeamPredictionChart from './modelOutputs/TeamPredictionChart';
import PlayerResultsGrid from './modelOutputs/PlayerResultsGrid';
import PlayerAccuracyTable from './modelOutputs/PlayerAccuracyTable';
import PlayerAvailabilityChart from './modelOutputs/PlayerAvailabilityChart';

/** Labelled 0-100 numeric input for prediction thresholds. */
function ThresholdInput({ label, value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-token-base font-normal text-muted-foreground">{label}</Label>
      <Input
        type="number"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
        className="w-[140px]"
      />
    </div>
  );
}

export default function ModelOutputs() {
  const { user } = useContext(AuthContext);
  const { activeLeague } = useContext(LeagueContext);

  // Metadata (loaded once)
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('team-results');

  // Tab-specific data (team predictions prefetched for the chart/accuracy
  // tabs, accuracy lazy loaded; the two grid tabs fetch their own data)
  const [teamPredictions, setTeamPredictions] = useState(null);
  const [teamPredictionsLoading, setTeamPredictionsLoading] = useState(false);

  const [playerAccuracy, setPlayerAccuracy] = useState(null);
  const [playerAccuracyLoading, setPlayerAccuracyLoading] = useState(false);

  const [refreshing, setRefreshing] = useState(false);
  // Incremented on refresh so the grid tabs refetch too
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters — starting/playing thresholds for the Player Accuracy tab
  const [startingThreshold, setStartingThreshold] = useState(50);
  const [playingThreshold, setPlayingThreshold] = useState(50);
  const [accuracyTeam, setAccuracyTeam] = useState(null);
  const [accuracySeason, setAccuracySeason] = useState(null);

  // Load metadata on mount
  const loadMetadata = useCallback(() => {
    if (!user || !activeLeague) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getModellingOutputs(user.user, activeLeague, user.gw_status)
      .then((response) => {
        setMetadata(response);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching metadata:', err);
        setError(err.message || 'Failed to load model outputs');
        setLoading(false);
      });
  }, [user, activeLeague]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // Prefetch team predictions as soon as metadata is available — the
  // distribution chart and the accuracy tab's season lookup need them.
  useEffect(() => {
    if (metadata && !teamPredictions && !teamPredictionsLoading) {
      setTeamPredictionsLoading(true);
      getTeamPredictions()
        .then((response) => {
          setTeamPredictions(response.predictions);
          setTeamPredictionsLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching team predictions:', err);
          toast.error('Failed to load team predictions');
          setTeamPredictionsLoading(false);
        });
    }
  }, [metadata, teamPredictions, teamPredictionsLoading]);

  // Load player accuracy when the accuracy or availability tab is active
  // (shared data). No team filter — all filtering done in frontend.
  useEffect(() => {
    if (
      (activeTab === 'player-accuracy' || activeTab === 'availability') &&
      !playerAccuracy &&
      !playerAccuracyLoading
    ) {
      setPlayerAccuracyLoading(true);
      getPlayerAccuracySummary()
        .then((response) => {
          setPlayerAccuracy(response.players);
          setPlayerAccuracyLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching player accuracy:', err);
          toast.error('Failed to load player accuracy data');
          setPlayerAccuracyLoading(false);
        });
    }
  }, [activeTab, playerAccuracy, playerAccuracyLoading]);

  // Refresh cached model output data without a full page reload
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const tasks = [
        getTeamPredictions().then((response) => setTeamPredictions(response.predictions)),
      ];
      if (playerAccuracy) {
        tasks.push(
          getPlayerAccuracySummary().then((response) => setPlayerAccuracy(response.players))
        );
      }
      await Promise.all(tasks);
      setRefreshKey((k) => k + 1);
      toast.success('Model outputs refreshed');
    } catch (err) {
      console.error('Error refreshing model outputs:', err);
      toast.error('Failed to refresh model outputs');
    } finally {
      setRefreshing(false);
    }
  }, [playerAccuracy]);

  // Build lookup maps from metadata
  const teamMap = useMemo(
    () =>
      new Map(
        (metadata?.teams || []).map((t) => [
          t.team_optaID,
          { fplID: t.team_fplID, name: t.name, short_name: t.short_name },
        ])
      ),
    [metadata?.teams]
  );

  const elementMap = useMemo(
    () =>
      new Map(
        (metadata?.elements || []).map((el) => [
          el.opta_playerID,
          {
            element: el.element,
            web_name: el.web_name,
            element_type: el.element_type,
            team_fplID: el.team_fplID,
          },
        ])
      ),
    [metadata?.elements]
  );

  const teamOptions = useMemo(
    () =>
      (metadata?.teams || [])
        .map((t) => ({ id: t.team_optaID, label: t.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [metadata?.teams]
  );

  const seasonOptions = useMemo(
    () =>
      (metadata?.seasons || [])
        .map((s) => ({ id: s.compSeasonID, label: s.season_label }))
        .sort((a, b) => b.label.localeCompare(a.label)), // Most recent first
    [metadata?.seasons]
  );

  // Loading state
  if (loading) {
    return <LoadingState message="Loading model outputs..." minHeight={300} />;
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className="max-w-xl">
        <AlertTitle>Error loading model outputs</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={loadMetadata}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!metadata) return null;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-token-xl font-semibold">Model Outputs</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh model output data"
          aria-label="Refresh model output data"
        >
          {refreshing ? <Loader2 className="animate-spin" /> : <RotateCw />}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="team-results">Team Results</TabsTrigger>
          <TabsTrigger value="team-distribution">Team Prediction Distribution</TabsTrigger>
          <TabsTrigger value="player-results">Player Results</TabsTrigger>
          <TabsTrigger value="player-accuracy">Player Accuracy Summary</TabsTrigger>
          <TabsTrigger value="availability">Player Availability Distribution</TabsTrigger>
        </TabsList>

        {/* Tab: Team Results — season × gameweek grid */}
        <TabsContent value="team-results">
          <TeamResultsGrid teamMap={teamMap} refreshKey={refreshKey} />
        </TabsContent>

        {/* Tab: Team Prediction Distribution */}
        <TabsContent value="team-distribution">
          {teamPredictionsLoading ? (
            <LoadingState message="Loading team predictions..." minHeight={200} />
          ) : (
            <TeamPredictionChart teamPredictions={teamPredictions || []} />
          )}
        </TabsContent>

        {/* Tab: Player Results — season × gameweek grid */}
        <TabsContent value="player-results">
          <PlayerResultsGrid teamMap={teamMap} elementMap={elementMap} refreshKey={refreshKey} />
        </TabsContent>

        {/* Tab: Player Accuracy Summary */}
        <TabsContent value="player-accuracy">
          <div className="mb-4 flex flex-wrap items-end gap-4">
            <FilterSelect
              label="Team"
              allLabel="All Teams"
              value={accuracyTeam}
              options={teamOptions}
              onChange={setAccuracyTeam}
            />
            <FilterSelect
              label="Season"
              allLabel="All Seasons"
              value={accuracySeason}
              options={seasonOptions}
              onChange={setAccuracySeason}
              className="h-9 w-[150px]"
            />
            <ThresholdInput
              label="Starting Threshold %"
              value={startingThreshold}
              onChange={setStartingThreshold}
            />
            <ThresholdInput
              label="Playing Threshold %"
              value={playingThreshold}
              onChange={setPlayingThreshold}
            />
          </div>
          {playerAccuracyLoading || teamPredictionsLoading ? (
            <LoadingState message="Loading player accuracy..." minHeight={200} />
          ) : (
            <PlayerAccuracyTable
              playerAccuracy={playerAccuracy || []}
              teamPredictions={teamPredictions || []}
              teamMap={teamMap}
              elementMap={elementMap}
              selectedTeam={accuracyTeam}
              selectedSeason={accuracySeason}
              startingThreshold={startingThreshold}
              playingThreshold={playingThreshold}
            />
          )}
        </TabsContent>

        {/* Tab: Player Availability Distribution */}
        <TabsContent value="availability">
          {playerAccuracyLoading ? (
            <LoadingState message="Loading availability data..." minHeight={200} />
          ) : (
            <PlayerAvailabilityChart availabilityData={playerAccuracy || []} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
